import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { SystemPageSeoResponse, SystemPageSeo } from '../models';

type AttrKey = 'name' | 'property';

export interface SeoApplyOptions {
  /**
   * Canonical URL to emit instead of `seo.og_url`.
   *
   * Pages that own their own URL shape (the event detail page, for example,
   * whose canonical must be the apex host plus the route path with no query
   * string) pass it here. Omit the key entirely to keep the historical
   * behaviour of canonicalising to `seo.og_url`; pass `null` to emit none.
   */
  canonical?: string | null;
}

@Injectable({ providedIn: 'root' })
export class SeoMetaService {
  #meta = inject(Meta);
  #title = inject(Title);
  #document = inject(DOCUMENT);

  /**
   * Apply an SEO + robots payload from the system-pages endpoint. Resets
   * robots/canonical state before optionally re-applying noindex, so callers
   * never have to think about leakage from prior routes.
   */
  apply(response: SystemPageSeoResponse): void {
    this.applySeo(response.seo);
    if (response.noindex) {
      this.#setRobotsNoindex();
    }
  }

  /**
   * Apply an SEO payload only. Always resets robots and canonical state, so
   * navigating from a noindex/canonical-bearing route to an "ordinary" one
   * cleanly clears prior values.
   *
   * `seo` is accepted as nullable because a degraded API response can omit the
   * block entirely; a missing block clears rather than throws.
   */
  applySeo(seo: SystemPageSeo | null | undefined, options: SeoApplyOptions = {}): void {
    const canonical = 'canonical' in options ? (options.canonical ?? null) : (seo?.og_url ?? null);

    if (seo?.meta_title && seo.meta_title.trim()) {
      this.#title.setTitle(seo.meta_title.trim());
    }

    this.#set('name', 'description', seo?.meta_description);
    this.#set('property', 'og:title', seo?.og_title);
    this.#set('property', 'og:description', seo?.og_description);
    this.#set('property', 'og:type', seo?.og_type);
    this.#set('property', 'og:url', seo?.og_url);
    this.#set('property', 'og:image', seo?.og_image_url);
    this.#set('name', 'twitter:card', seo?.twitter_card);
    this.#set('name', 'twitter:title', seo?.twitter_title);
    this.#set('name', 'twitter:description', seo?.twitter_description);
    this.#set('name', 'twitter:image', seo?.twitter_image_url);

    this.#clearRobots();
    if (seo?.noindex) {
      this.#setRobotsNoindex();
    }

    this.#setCanonical(canonical);
  }

  #setRobotsNoindex(): void {
    this.#meta.updateTag({ name: 'robots', content: 'noindex,nofollow' }, 'name="robots"');
  }

  #clearRobots(): void {
    this.#meta.removeTag('name="robots"');
  }

  #setCanonical(url: string | null): void {
    const head = this.#document.head;
    const existing = head.querySelector('link[rel="canonical"]');
    const href = url?.trim();

    if (!href) {
      existing?.remove();
      return;
    }

    // Replace, never append: client-side navigation between two pages must
    // leave exactly one canonical behind (E-10).
    if (existing) {
      existing.setAttribute('href', href);
      return;
    }

    const link = this.#document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', href);
    head.appendChild(link);
  }

  /**
   * Write a meta tag, or remove it when there is no value to write. A
   * whitespace-only value counts as no value: `content=""` (or `content="  "`)
   * must never be emitted.
   */
  #set(attr: AttrKey, key: string, value: string | null | undefined): void {
    const selector = `${attr}="${key}"`;
    const content = value?.trim();

    if (content) {
      this.#meta.updateTag({ [attr]: key, content }, selector);
    } else {
      this.#meta.removeTag(selector);
    }
  }
}
